import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { signup } from '../../api/client';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

export default function Signup() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || !turnstileRef.current) return;

    function renderWidget() {
      if (window.turnstile && turnstileRef.current) {
        turnstileRef.current.innerHTML = '';
        window.turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token) => setTurnstileToken(token),
        });
      }
    }

    if (window.turnstile) {
      renderWidget();
    } else {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.onload = () => {
        // Small delay to let Turnstile initialize
        setTimeout(renderWidget, 100);
      };
      document.head.appendChild(script);
    }
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setError('Please complete the verification check');
      return;
    }
    setLoading(true);
    try {
      await signup(name, email, password, turnstileToken);
      setDone(true);
    } catch (err) {
      setError(err.message || 'Failed to create account');
      // Reset Turnstile
      if (window.turnstile && turnstileRef.current) {
        window.turnstile.reset(turnstileRef.current);
        setTurnstileToken('');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-navy flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/bcd-icon.png" alt="BCD" className="h-10 mx-auto mb-3" />
          <h1 className="text-2xl font-heading font-bold text-white uppercase tracking-wide">
            Try The Clarity Map
          </h1>
          <p className="text-gray-400 text-sm mt-1">Free 7-day trial. No credit card required.</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          {done ? (
            <div className="text-center">
              <p className="text-txt-primary mb-2">
                Account created!<br />Check your email for a verification link.
              </p>
              <p className="text-sm font-bold text-txt-primary mb-4">
                Don't see it? Check your spam or junk folder.
              </p>
              <Link to="/login" className="text-gold-dark font-semibold hover:underline">
                Back to login
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <input
                type="text"
                placeholder="Full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
              />
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
              />
              <input
                type="password"
                placeholder="Password (12+ characters)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
              />
              <input
                type="password"
                placeholder="Confirm password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                className="border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
              />
              <p className="text-xs text-gray-500">
                12+ characters. Must include uppercase, lowercase, number, and special character.
              </p>
              {TURNSTILE_SITE_KEY && <div ref={turnstileRef} className="flex justify-center" />}
              {error && <p className="text-red-500 text-sm">{error}</p>}
              <button
                type="submit"
                disabled={loading}
                className="btn-gold w-full py-3 text-sm font-semibold disabled:opacity-50"
              >
                {loading ? 'Creating account...' : 'Start Free Trial'}
              </button>
              <Link to="/login" className="text-center text-sm text-gold-dark font-semibold hover:underline">
                Already have an account? Sign in
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
